/**
 * Discloses citations for ONE answer that never anchored to a sentence.
 *
 * This lives per-answer, inline, rather than in ThreadReferencesSidebar,
 * because each answer has its own citation numbering (index 1 in an older
 * answer means something different from index 1 in the newest one) and the
 * sidebar only ever renders the newest grounded answer. Rendering it in the
 * rail hid every unanchored citation belonging to an earlier answer in a
 * multi-question thread.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { KnowledgeAskCitation } from '@digital-twins/shared-types'

import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { CITATION_MARKER_CHIP_CLASS } from './ThreadCitationMarker'
import {
  citationSocialPost,
  SocialCitationPreview,
  socialCitationExcerpt,
} from '../KnowledgeHubPage/social/SocialCitationPostMeta'

interface ThreadUnplacedSourcesProps {
  /** 1-based citation indexes for THIS answer that never anchored to a
   * sentence. Ascending. Omitted or empty renders nothing. */
  indexes?: Array<number>
  /** This answer's citations, so a cited social post lists as the post itself. */
  citations?: Array<KnowledgeAskCitation>
  onOpen?: (index: number, citation: KnowledgeAskCitation) => void
}

export function ThreadUnplacedSources(props: ThreadUnplacedSourcesProps) {
  const { indexes, citations, onOpen } = props

  const { t } = useTranslation('app')

  const [expanded, setExpanded] = useState(false)

  if (!indexes || indexes.length === 0) {
    return null
  }

  return (
    <div className="max-w-prose pt-1" data-testid="thread-unplaced-sources">
      {/*
        Tailwind v3 preflight sets no pointer cursor on `button`, so without
        `cursor-pointer` this reads as a caption and gives an arrow cursor on
        a control that is genuinely clickable. Hover treatment matches the
        other muted disclosures (ThreadSourcePanel), and `-mx-2` cancels the
        padding so the label still aligns with the prose above.
      */}
      <button
        type="button"
        className="group -mx-2 flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-left transition-colors hover:bg-muted/60"
        data-testid="thread-unplaced-sources-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
      >
        <Text
          as="span"
          size="xs"
          color="muted"
          className="transition-colors group-hover:text-foreground"
        >
          {t('threads.detail.unplaced', { count: indexes.length })}
        </Text>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-[transform,color] group-hover:text-foreground',
            expanded ? 'rotate-180' : '',
          )}
          aria-hidden
        />
      </button>
      {expanded ? (
        <ul
          className="mt-2 flex flex-wrap gap-1.5"
          data-testid="thread-unplaced-sources-list"
        >
          {indexes.map((index) => {
            const citation = citations?.[index - 1]
            const socialPost = citation ? citationSocialPost(citation) : null
            if (citation && socialPost) {
              return (
                <li key={index} className="w-full">
                  <button
                    type="button"
                    className="flex w-full cursor-pointer items-start gap-2 rounded-lg border border-border px-2 py-1.5 text-left transition-colors hover:bg-muted/60"
                    data-testid={`thread-unplaced-social-${index}`}
                    onClick={() => onOpen?.(index, citation)}
                  >
                    <span
                      className={cn(
                        CITATION_MARKER_CHIP_CLASS,
                        'mt-0.5 bg-muted-foreground/15 text-muted-foreground',
                      )}
                    >
                      {index}
                    </span>
                    <SocialCitationPreview
                      post={socialPost}
                      excerpt={socialCitationExcerpt(citation)}
                    />
                  </button>
                </li>
              )
            }
            return (
            <li key={index}>
              <span
                className={cn(
                  CITATION_MARKER_CHIP_CLASS,
                  'bg-muted-foreground/15 text-muted-foreground',
                )}
              >
                {index}
              </span>
            </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
